import { defineStore } from 'pinia'
import axiosSalesAi, { type _AxiosError } from './api'
import type { ApiErrorEntry } from '@/types/api-error.type'
import {
  ConversationAgent,
  CONVERSATION_TITLE_MAX_LENGTH,
  type ActiveTxResponse,
  type ChatMessage,
  type ChatMessageAdditionalData,
  type ChatTransaction,
  type ConversationDetail,
} from '@/types/conversation.type'

// 1 操作で複数のエンドポイントが落ちるため、直近の数操作分を相関材料として残す
const MAX_ERROR_ENTRIES = 20

interface State {
  errorEntries: ApiErrorEntry[]
  // 診断中（バナー表示中）のエラー。ApiErrorAssist が書き、他の overlay が表示の調停に読む
  askedEntry: ApiErrorEntry | null
  messages: ChatMessage[]
  activeTx: ChatTransaction | null
}

// polling と post が並走すると同じ message が両方から届くため id で重複を弾く
function appendNewMessages(state: State, incoming: ChatMessage[]) {
  const existingIds = new Set(state.messages.map((m) => m.id))
  state.messages.push(...incoming.filter((m) => !existingIds.has(m.id)))
}

// 診断は画面の裏で走る。conversationStore と同じ endpoint を叩くが、currentConversation /
// activeTx は重なっている元の画面のもので、書き換えてはいけない。失敗の toast も出さない。
// 診断の起動・polling・状態遷移は useErrorAssistDiagnosis の責務で、ここは API call と
// その結果、および診断中のエラー（askedEntry）の保持だけを担う
export const useErrorAssistStore = defineStore('errorAssist', {
  state: (): State => ({
    errorEntries: [],
    askedEntry: null,
    messages: [],
    activeTx: null,
  }),

  getters: {
    answer(state): string {
      for (let i = state.messages.length - 1; i >= 0; i--) {
        const m = state.messages[i]
        if (m.role === 'assistant' && m.text.trim()) return m.text
      }
      return ''
    },
  },

  actions: {
    recordError(entry: ApiErrorEntry) {
      this.errorEntries = [entry, ...this.errorEntries].slice(0, MAX_ERROR_ENTRIES)
    },
    setAskedEntry(entry: ApiErrorEntry | null) {
      this.askedEntry = entry
    },
    // entries は意図的に残す（理由は MAX_ERROR_ENTRIES）。askedEntry も診断の再開始時に呼ばれるため残す
    resetDiagnosis() {
      this.$patch((state) => {
        state.messages = []
        state.activeTx = null
      })
    },
    createConversation(title: string) {
      return new Promise<number>((resolve, reject) => {
        axiosSalesAi
          .post(
            '/conversations',
            {
              title: title.slice(0, CONVERSATION_TITLE_MAX_LENGTH),
              agent: ConversationAgent.errorAssist,
            },
            { backgroundRequest: true },
          )
          .then((res: { data: ConversationDetail }) => {
            resolve(res.data.id)
          })
          .catch((err: _AxiosError) => {
            console.error(err)
            reject(err)
          })
      })
    },
    postChatMessage(
      text: string,
      conversationId: number,
      additionalData: ChatMessageAdditionalData,
    ) {
      const formData = new FormData()
      formData.append('message', text)
      formData.append('data', JSON.stringify(additionalData))
      return new Promise<void>((resolve, reject) => {
        axiosSalesAi
          .post(`/conversations/${conversationId}/chat-messages`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            backgroundRequest: true,
          })
          .then((res: { data: { tx: ChatTransaction; messages: ChatMessage[] } }) => {
            this.$patch((state) => {
              appendNewMessages(state, res.data.messages)
              state.activeTx = res.data.tx
            })
            resolve()
          })
          .catch((err: _AxiosError) => {
            console.error(err)
            reject(err)
          })
      })
    },
    fetchActiveTx(conversationId: number, lastMessageId?: number) {
      return new Promise<void>((resolve, reject) => {
        axiosSalesAi
          .get(`/conversations/${conversationId}/chat-transactions/active-tx`, {
            params: {
              lastMessageId,
            },
            backgroundRequest: true,
          })
          .then((res: { data: ActiveTxResponse }) => {
            this.$patch((state) => {
              appendNewMessages(state, res.data.newMessages)
              state.activeTx = res.data.activeTx
            })
            resolve()
          })
          .catch((err: _AxiosError) => {
            console.error(err)
            reject(err)
          })
      })
    },
  },
})
